import React, { useMemo, useCallback, useEffect, useState } from "react";
import { CustomerSucessController, Store__System } from "@talentbait/shared-components";
import logger from "../../../../../utils/LoggerFrontend";
import withRefluxStore from "../../../../../Stores/withRefluxStore.jsx";
import TemplatePostStore from "../TemplatePostStore";
import TemplatePostActions from "../TemplatePostActions";
import { IMAGE_TEMPLATES, VIDEO_TEMPLATES } from "@talentbait/remotion-templates-talentbait";
import TemplatePostEditor from "../TemplatePost__TemplateEditor/TemplateEditor.jsx";
import PhotoEditor from "../TemplatePost__PhotoEditor/TemplatePostPhotoEditor.jsx";
import path from "path-browserify";

import {
  TemplateWrapper,
  MainWrapper,
  CreativeAndUploaderWrapper,
  UploaderWrapper,
  UploadCloudIcon,
  TemplateEditorOpenButton
} from "./styles";
import LoadingIndicator from "react-loading-indicator";
import { removeShutterstockTransformations } from "../../utils";
import Actions from "../../../../../Actions";
import VideoGeneratorPreview from "../../VideoGenerator/VideoGenerator__Preview/VideoGenerator__Preview.jsx";

import usePrevious from "../../../../../hooks/usePrevious";
import PreviewStore from "../../../../../Stores/PreviewStore.js";
import {
  checkIfLandscape,
  getVideoDuration,
  getVideoSize,
  isOldDefaultTemplateStyle
} from "./utils";
import GlobalVideoGeneratorPreview from "../../VideoGenerator/GlobalVideoGeneratorPreview/GlobalVideoGeneratorPreview";
import useVideoBackComp from "./hooks/useVideoBackComp";
import {
  PhotoWrapper,
  TIKTOK_TEMPLATE_PREVIEW_SIZE_SPEC,
  TikTokWrapper
} from "../../PostEditors/post-editor-tiktok/PostEditorTikTok.styled.js";

const TEMPLATES = { ...IMAGE_TEMPLATES, ...VIDEO_TEMPLATES };

const TemplatePostPreview = ({
  // Parent props
  orgaId,
  mediaAssets = [],
  creatives = [],
  creativeAssets = [],
  channel = "",
  videoData,
  selectedMediaAssetIndexToEdit,
  updatePost,
  isVideoGeneratorOpen,
  templatePreviewSizes = null,
  extraAspectRatios = [],
  // Store
  template,
  templateId,
  inputProps,
  editable = true,
  showPictureEditor,
  selectedCreativeKey,
  isFetchingMediaAsset,
  isGeneratingMediaAsset,
  templateType,
  campaignData,
  userProfile
}) => {
  logger.debug("TemplatePostPreview", "called", {
    mediaAssets,
    creatives,
    selectedMediaAssetIndexToEdit,
    userProfile,
    template,
    channel,
    campaignData,
    templateType
  });

  const isTikTokVideoTemplate = template?.id === "TikTokVideo";
  const { companyId, companyName } = campaignData || {};
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);

  if (!isTikTokVideoTemplate) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useVideoBackComp({ inputProps, creatives, mediaAssets, updatePost });
  }

  const [isLandScape, setIsLandScape] = useState(false);

  const [currentInputProps, setCurrentInputProps] = useState(inputProps);
  const TemplateComponent = useMemo(() => TEMPLATES[template?.id], [template]);
  const previousInputProps = usePrevious(inputProps);

  const isOldCampaign = isOldDefaultTemplateStyle(campaignData?.status, campaignData?.createdDate);

  // Set the input props to the video data if the template is a video
  useEffect(() => {
    let newInputProps = { ...inputProps, isNewCampaign: !isOldCampaign };

    if (templateType === "video" && channel === "tiktok") {
      newInputProps = { ...videoData };
    }

    // Compare the previous input props with the new input props and update the store if they are different
    if (JSON.stringify(previousInputProps) !== JSON.stringify(newInputProps)) {
      TemplatePostActions.updateInputProps(newInputProps);
    }
  }, [templateType]);

  const mediaAssetId = useMemo(
    () => mediaAssets?.[selectedMediaAssetIndexToEdit],
    [mediaAssets, selectedMediaAssetIndexToEdit]
  );

  const selectedCreative = useMemo(
    () => creatives?.[selectedMediaAssetIndexToEdit],
    [creatives, selectedMediaAssetIndexToEdit]
  );

  const [selectedAsset, setSelectedAsset] = useState(null);
  const updateSelectedAsset = useCallback(
    (asset) => {
      setSelectedAsset(asset);

      logger.log("TemplatePostPreviews", "updateSelectedAsset", {
        ...asset,
        ...campaignData
      });

      // Campaign data for segment tracking purposes. Fetching it from the Preview store
      const { id: campaignId, orgaName: companyName, jobTitle } = campaignData || {};

      // Segment tracking
      CustomerSucessController.send("Image Selected", {
        campaignId,
        orgaId,
        companyName,
        jobTitle,
        ...asset
      });
    },
    [campaignData, orgaId]
  );

  useEffect(() => {
    setCurrentInputProps(inputProps);
  }, [inputProps]);

  useEffect(() => {
    TemplatePostActions.updateCurrentInputProps(currentInputProps);
  }, [currentInputProps]);

  useEffect(() => {
    // If we have a media asset record then fetch and populate media asset settings
    if (mediaAssetId) {
      TemplatePostActions.getMediaAssetWithId(mediaAssetId);
      return;
    }

    // If we don't have a media asset record then generate a default media asset based on the creative
    if (!isGeneratingMediaAsset && creatives[selectedMediaAssetIndexToEdit]) {
      const { organizationId } = campaignData || {};

      const ref = TemplatePostActions.generateDefaultMediaAsset.completed.listen((mediaAssetId) => {
        logger.debug(
          "TemplatePostPreview:generateDefaultMediaAssetCompleted",
          "successfully generated media asset record",
          { mediaAssetId, creatives, mediaAssets }
        );
        const newMediaAssets = [...mediaAssets];
        newMediaAssets[selectedMediaAssetIndexToEdit] = mediaAssetId;
        updatePost({ mediaAssets: newMediaAssets });
        ref();
      });
      const refFailed = TemplatePostActions.generateDefaultMediaAsset.failed.listen((error) => {
        logger.log(
          "error:TemplatePostPreview:generateDefaultMediaAssetFailed",
          "unable to generate media asset",
          { error }
        );
        refFailed();
      });

      const creative = creatives[selectedMediaAssetIndexToEdit];
      logger.debug("TemplatePostPreview", "about to generate media asset", { creative });

      TemplatePostActions.generateDefaultMediaAsset(creative, organizationId);

      return;
    }
  }, [mediaAssetId, creatives, mediaAssets]);

  useEffect(() => {
    if (isVideoGeneratorOpen) {
      return setIsTemplateEditorOpen(true);
    }

    return setIsTemplateEditorOpen(false);
  }, [isVideoGeneratorOpen]);

  const openTemplateEditor = useCallback(() => {
    setIsTemplateEditorOpen(true);
  }, [setIsTemplateEditorOpen]);

  const closeTemplateEditor = useCallback(() => {
    setIsTemplateEditorOpen(false);
    setCurrentInputProps(inputProps);
  }, [setIsTemplateEditorOpen, inputProps]);

  const saveInputProps = useCallback(
    (newInputProps = {}) => {
      if (selectedAsset) {
        // Shutterstock use case
        const extension = path.extname(selectedAsset.url);

        const data = {
          externalId: selectedAsset.imageId,
          searchId: selectedAsset.searchId,
          url: selectedAsset.url,
          source: selectedAsset.source,
          assetType: "creative",
          mediaType: "image",
          extension
        };

        Actions.saveAssets(orgaId, companyId, [data]);

        logger.log("Campaign__Create_PhotoEditor", "closeAndSaveCallback template post", {
          ...data,
          ...campaignData,
          ...updatePost
        });

        // Campaign data for segment tracking purposes. Fetching it from the Preview store
        const {
          id: campaignId,
          orgaName: companyName,
          organizationId,
          jobTitle
        } = campaignData || {};
        const { userName } = userProfile || {};

        // Segment tracking
        CustomerSucessController.send("Image Applied", {
          origin: "TemplatePostPreview",
          campaignId,
          orgaId: organizationId,
          companyName,
          jobTitle,
          userName,
          ...data
        });

        const ref = Actions.saveAssets.completed.listen((result) => {
          const assetId = result.meta.payload[0];
          updatePostHandler({ newInputProps, assetId });
          ref();
        });

        const refFailed = Actions.saveAssets.failed.listen(() => {
          updatePostHandler({ newInputProps });
          refFailed();
        });
      } else {
        updatePostHandler({ newInputProps });
      }
    },
    [
      currentInputProps,
      mediaAssetId,
      template,
      creatives,
      mediaAssets,
      selectedCreative,
      selectedMediaAssetIndexToEdit,
      updatePost,
      setIsTemplateEditorOpen,
      campaignData,
      orgaId,
      userProfile
    ]
  );

  const updatePostHandler = ({ newInputProps, assetId }) => {
    // If the image selected is different from what we have on the creatives, then update the creatives.
    // This is to show the creatives immediately inside the performance ads previews

    const finalInputProps = { ...currentInputProps, ...newInputProps };

    const newCreatives = [...creatives];
    const assetObjects = [...creativeAssets];

    if (assetId) {
      const index = assetObjects.map((asset) => asset.id).indexOf(assetId);
      if (index >= 0) {
        assetObjects[index] = {
          id: assetId,
          orientation: template.orientation
        };
      } else {
        assetObjects.push({
          id: assetId,
          orientation: template.orientation
        });
      }
    }

    newCreatives[selectedMediaAssetIndexToEdit] = finalInputProps?.backgroundImage;
    const creativesWithoutTransformations = newCreatives.map(removeShutterstockTransformations);

    const updates = {
      creatives: newCreatives,
      creativeAssets: assetObjects
    };

    Actions.getAssets(assetObjects.map((asset) => asset.id));

    const ref = Actions.getAssets.completed.listen((result) => {
      if (templateType === "image") {
        let assets = result.meta.payload;

        // 1. We remove the assets we are not using anymore
        assets = assets.filter(
          (asset) =>
            creativesWithoutTransformations.includes(asset.url) ||
            newCreatives.includes(asset.shutterstock.unlicensedUrl)
        );

        // 2. We select the attributes that we want to store to creativeAssets
        assets = assets.map((asset) => ({
          id: asset.id,
          orientation: assetObjects.filter((assetObject) => asset.id === assetObject.id)[0][
            "orientation"
          ]
        }));

        updates.creativeAssets = assets;
        updatePost(updates);
        ref();
      }
    });

    const refFailed = Actions.getAssets.failed.listen(() => {
      if (templateType === "image") {
        updatePost(updates);
        refFailed();
      }
    });

    TemplatePostActions.updateInputProps(finalInputProps);

    if (mediaAssetId) {
      TemplatePostActions.updateMediaAsset(mediaAssetId, {
        inputProps: finalInputProps,
        templateId: template?.id,
        updatedDate: new Date().toISOString(),
        type: template?.type
      });
    }

    if (isTikTokVideoTemplate) {
      updatePost(finalInputProps);
    }

    setIsTemplateEditorOpen(false);
  };

  const channelsVerticalImageAllowed = [
    "youtube",
    "google-display",
    "ebay-kleinanzeigen",
    "instagram",
    "facebook",
    "snapchat"
  ];

  const shouldShowVerticalImage = channelsVerticalImageAllowed.includes(channel);
  const isSnapchatChannel = channel === "snapchat";

  const shouldShowTikTokVideoTemplate = channel === "tiktok";

  const defaultTemplatePreviewDimensions = {
    height: template.height,
    width: template.width,
    minHeight: template.height,
    maxHeight: template.height,
    minAspect: template.aspectRatio,
    maxAspect: template.aspectRatio
  };

  const imagePreviewSizes = templatePreviewSizes || defaultTemplatePreviewDimensions;

  const allowVideoUploads = selectedCreativeKey.toLowerCase()?.includes("video");
  const allowImageUploads =
    selectedCreativeKey.toLowerCase()?.includes("image") ||
    selectedCreativeKey.toLowerCase()?.includes("logo");

  if (currentInputProps.video && !currentInputProps.duration && !isTikTokVideoTemplate) {
    getVideoDuration(currentInputProps.video)
      .then((duration) => {
        const newInputProps = { ...currentInputProps, duration: duration };
        TemplatePostActions.updateInputProps(newInputProps);
      })
      .catch((error) => {
        logger.error("error:TemplatePostPreview", "GetVideoDuration", {
          error
        });
      });
  }

  // Check if the template is a landscape version of the image template or a landscape video template
  useEffect(() => {
    const showLandscape = checkIfLandscape(template, currentInputProps?.orientation);
    setIsLandScape(showLandscape);
  }, [currentInputProps, template]);

  const pictureEditor = (
    <div style={{ position: "absolute", width: "100%", height: "100%" }}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <PhotoEditor
          selectedCreativeIndexToEdit={0}
          creatives={[currentInputProps[selectedCreativeKey]]}
          allowVideos={allowVideoUploads}
          allowImages={allowImageUploads}
          canBeEmpty={selectedCreativeKey?.includes("logo")}
          updatePost={({ value }) => {
            setCurrentInputProps({ ...currentInputProps, [selectedCreativeKey]: value[0] });
          }}
          cancelUpdate={() => {
            setCurrentInputProps({
              ...currentInputProps,
              [selectedCreativeKey]: inputProps[selectedCreativeKey]
            });
            setIsTemplateEditorOpen(true);
          }}
          saveUpdate={(data) => saveInputProps(data)}
          updateSelectedAsset={updateSelectedAsset}
          minHeight={selectedCreativeKey?.includes("logo") ? 50 : imagePreviewSizes.minHeight}
          maxHeight={selectedCreativeKey?.includes("logo") ? 50 : imagePreviewSizes.maxHeight}
          minAspect={isLandScape ? imagePreviewSizes.maxAspect : imagePreviewSizes.minAspect}
          maxAspect={isLandScape ? imagePreviewSizes.maxAspect : imagePreviewSizes.minAspect}
          extraAspectRatios={extraAspectRatios}
          width={selectedCreativeKey?.includes("logo") ? 50 : imagePreviewSizes.width}
          editable={editable}
          preopenMediaLibrary={true}
          isLogo={selectedCreativeKey?.includes("logo")}
          isImageTemplate={templateType === "image"}
          selectedAsset={selectedAsset}
          templateId={template?.id}
          isLandScape={isLandScape}
        />
      </div>
    </div>
  );

  const tikTokVideoPreview = (
    <TikTokWrapper
      width={TIKTOK_TEMPLATE_PREVIEW_SIZE_SPEC.width}
      height={TIKTOK_TEMPLATE_PREVIEW_SIZE_SPEC.height}
    >
      <PhotoWrapper maxHeight={TIKTOK_TEMPLATE_PREVIEW_SIZE_SPEC.maxHeight}>
        <VideoGeneratorPreview
          height={TIKTOK_TEMPLATE_PREVIEW_SIZE_SPEC.height}
          width={TIKTOK_TEMPLATE_PREVIEW_SIZE_SPEC.width}
          videoData={inputProps}
        />
        {showPictureEditor && pictureEditor}
        <TemplatePostEditor
          isTemplateEditorOpen={isTemplateEditorOpen}
          openTemplateEditor={openTemplateEditor}
          closeTemplateEditor={closeTemplateEditor}
          updateInputProps={setCurrentInputProps}
          saveInputProps={saveInputProps}
          template={template}
          inputProps={currentInputProps}
          allowImages={false}
          showTikTokVideoTemplate={shouldShowTikTokVideoTemplate}
          isSnapchatChannel={isSnapchatChannel}
        />
      </PhotoWrapper>
    </TikTokWrapper>
  );

  const imageEditor =
    !isFetchingMediaAsset && !isGeneratingMediaAsset ? (
      <TemplatePostEditor
        isTemplateEditorOpen={isTemplateEditorOpen}
        openTemplateEditor={openTemplateEditor}
        closeTemplateEditor={closeTemplateEditor}
        updateInputProps={setCurrentInputProps}
        saveInputProps={saveInputProps}
        template={template}
        inputProps={currentInputProps}
        showVerticalImage={shouldShowVerticalImage}
        showTikTokVideoTemplate={shouldShowTikTokVideoTemplate}
        allowVideos={true}
        isSnapchatChannel={isSnapchatChannel}
      />
    ) : null;

  const emitNewImageSelection = (asset) => {
    setCurrentInputProps({ ...currentInputProps, [selectedCreativeKey]: asset });
    setIsTemplateEditorOpen(true);
    TemplatePostActions.updateShowPictureEditor(false);
  };

  const imagePreview = () => {
    // If the main image is being edited then hide the image from the preview since it will be
    // shown by the PhotoEditor above (with the image cropper). Logos should not be hidden
    const selectedCreativeValue = showPictureEditor
      ? selectedCreativeKey.includes("logo")
        ? currentInputProps[selectedCreativeKey]
        : ""
      : currentInputProps[selectedCreativeKey];

    const defaultHeight = isLandScape ? imagePreviewSizes.minHeight : imagePreviewSizes.maxHeight;

    const { videoHeight, videoWidth } = getVideoSize(
      templateId,
      imagePreviewSizes.width,
      defaultHeight
    );

    return (
      <>
        {showPictureEditor && pictureEditor}

        <div style={{ pointerEvents: showPictureEditor ? "none" : "all", zIndex: 3 }}>
          <CreativeAndUploaderWrapper id="creative-and-uploader-wrapper">
            <TemplateWrapper
              minHeight={imagePreviewSizes.minHeight}
              maxHeight={imagePreviewSizes.maxHeight}
              width={imagePreviewSizes.width}
              height={defaultHeight}
            >
              {isFetchingMediaAsset || isGeneratingMediaAsset ? (
                <LoadingIndicator />
              ) : (
                <>
                  {templateType === "image" ? (
                    <TemplateComponent
                      {...{
                        ...currentInputProps,
                        [selectedCreativeKey]: selectedCreativeValue
                      }}
                      width={imagePreviewSizes.width}
                    />
                  ) : (
                    !showPictureEditor && (
                      <GlobalVideoGeneratorPreview
                        remotionTemplate={TemplateComponent}
                        width={videoWidth}
                        height={videoHeight}
                        duration={isTikTokVideoTemplate ? 600 : null}
                        videoData={{ ...currentInputProps }}
                      />
                    )
                  )}
                </>
              )}
            </TemplateWrapper>
            {!isTemplateEditorOpen && editable && (
              <UploaderWrapper>
                <TemplateEditorOpenButton onClick={openTemplateEditor}>
                  <UploadCloudIcon src="/images/icon-new__edit.svg" small={false} alt="" />
                </TemplateEditorOpenButton>
              </UploaderWrapper>
            )}
          </CreativeAndUploaderWrapper>
          {imageEditor}
        </div>
      </>
    );
  };

  const isTikTok = templateId === "TikTokVideo" && channel === "tiktok";

  logger.debug("TemplatePost debug", "called", {
    inputProps,
    currentInputProps
  });

  return <MainWrapper>{isTikTok ? tikTokVideoPreview : imagePreview()}</MainWrapper>;
};

export default withRefluxStore(
  TemplatePostPreview,
  [TemplatePostStore, PreviewStore, Store__System],
  [
    "template",
    "templateId",
    "orientation",
    "inputProps",
    "showPictureEditor",
    "selectedCreativeKey",
    "isFetchingMediaAsset",
    "isGeneratingMediaAsset",
    "templateType",
    "campaignData",
    "userProfile"
  ]
);
